export const en = {
  code: "en",
  label: "English",
  ui: {
    skipToContent: "Skip to content",
    mainNav: "Main navigation",
    mobileNav: "Mobile navigation",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    sending: "Sending …",
    languageLabel: "Language",
    backToHome: "Back to home",
    legalPlaceholder: "Placeholder: this text must be replaced with the operator's real details before going live.",
    legalTranslationNote: "The German version is legally binding. This translation is provided for convenience only.",
    corporateLogin: "Business"
  },
  brand: {
    name: ["Positiv", "up"],
    tagline: "See. Measure. Grow.",
    domain: "positivup.com"
  },
  nav: [{
    label: "What we measure",
    href: "#leistungen"
  }, {
    label: "Difference",
    href: "#unterschied"
  }, {
    label: "How it works",
    href: "#ablauf"
  }, {
    label: "Pricing",
    href: "#preise"
  }, {
    label: "FAQ",
    href: "#faq"
  }],
  cta: "Free check",
  hero: {
    eyebrow: "A digital audit for local businesses",
    headline: ["The digital X-ray", "of your business"],
    subline: "We scan your entire digital substance — from day one until today. The report shows the strengths and weaknesses of your digital infrastructure and checks whether it still holds up in the age of AI assistants.",
    reassurance: "Free · No login · Result as a link",
    cta: {
      primary: "Free analysis",
      whatsapp: "Contact on WhatsApp",
      whatsappHref: "https://wa.me/"
    },
    panel: {
      title: "Excerpt from the findings",
      caption: "Illustrative example with sample values.",
      history: {
        label: "Digital trail",
        value: "Active since 2014",
        note: "11 years of history reviewed: domain, profiles, listings, content."
      },
      ai: {
        label: "AI readiness of your infrastructure",
        value: "82 % prepared",
        note: "Machine-readable structure, clear details, quotable content."
      },
      local: {
        label: "Local discoverability",
        value: "Position 4",
        note: "Only a small part of the report — map pins are the start, not the result."
      },
      score: {
        label: "Overall finding",
        caption: "out of 100 points"
      }
    }
  },
  problem: {
    title: "Why you never notice it",
    lead: "You don't lose customers because someone turned you down. You lose them because you were never in the running.",
    cards: [{
      title: "The search ends at the map results",
      body: "Most people type “hairdresser near me” and decide from the first few map results. What sits below is rarely opened."
    }, {
      title: "AI assistants answer directly",
      body: "Asked “which hairdresser is good in Elberfeld”, ChatGPT and Claude now name specific businesses — with web search, from reviews and websites."
    }, {
      title: "Invisible means: never compared",
      body: "If you're missing from the map and the AI answer, you never even enter the competition. Price, quality and service stop mattering."
    }]
  },
  audience: {
    title: "Who should use this service?",
    lead: "If any of these situations apply to your business, our digital asset analysis can help you become more visible.",
    items: [{
      title: "You aren't getting enough customers",
      body: "If customer numbers fall below your expectations, the problem is rarely in one place — it's hidden across your entire digital presence."
    }, {
      title: "You can't be found on Google Maps or search",
      body: "If potential customers can't find your business on Google, you have a serious issue with both map and search rankings."
    }, {
      title: "AI tools like ChatGPT never recommend you",
      body: "Customers are increasingly asking AI assistants instead of Google. If your name never appears in this new search behaviour, you're already losing future customers."
    }, {
      title: "Your competitors look more visible than you",
      body: "If competitors in the same industry appear before you in search, maps and AI recommendations, you need to know what they're doing differently."
    }, {
      title: "There may be risks on your website you haven't noticed",
      body: "Most business owners don't know whether their site has legal or technical gaps — these are problems that grow unnoticed."
    }],
    note: "If any of these situations sound familiar, start with the free analysis.",
    ctaTitle: "Let us prepare your custom report!",
    ctaBody: "Find out where you stand right now in 2 minutes with the free analysis.",
    ctaButton: "Get free report →"
  },
  areas: {
    title: "What we measure",
    lead: "One score out of 100 points across six areas — each area built from individual, traceable checks.",
    items: [{
      icon: "monitor",
      label: "Website & technology",
      max: 18,
      line: "Load time, mobile rendering, encryption and the technical basics of your site."
    }, {
      icon: "search",
      label: "On-page SEO",
      max: 20,
      line: "Titles, headings, copy and structure — does Google understand what you offer?"
    }, {
      icon: "mapPin",
      label: "Local SEO",
      max: 13,
      line: "Address, opening hours, directory listings and how findable you are nearby."
    }, {
      icon: "store",
      label: "Google Business Profile",
      max: 20,
      line: "Completeness, categories, photos, posts and your position on the map."
    }, {
      icon: "star",
      label: "Reviews",
      max: 17,
      line: "Volume, recency, distribution, reply behaviour and recurring themes."
    }, {
      icon: "sparkles",
      label: "AI visibility",
      max: 12,
      line: "Are you named when people ask AI assistants for real recommendations — and how?"
    }],
    highlights: [{
      icon: "scale",
      label: "Legal risk & image copyright check",
      line: "Imprint, cookie consent and whether photos across your whole site carry copyright risk — with evidence links."
    }, {
      icon: "target",
      label: "Competitor comparison",
      line: "Your region's top 3 competitors — compared not just on overall score, but on every sub-category such as SEO, reputation and AI visibility."
    }, {
      icon: "route",
      label: "Prioritized 3-month action plan",
      line: "Every finding is reviewed by an expert and turned into a concrete, prioritised roadmap sorted by impact and effort."
    }]
  },
  different: {
    title: "What Positivup does differently",
    lead: "Not a dashboard full of metrics, but an assessment you or your agency can act on right away.",
    steps: [{
      kicker: "Step 1",
      title: "Maps position and visibility heatmap",
      body: "Your position isn't measured at a single point but across a grid of measuring points over the city — typically 4×4 to 6×6, depending on the area. Rank 1 at your own address and invisible three streets away is the norm. The organic Google position is measured separately and reported on its own."
    }, {
      kicker: "Step 2",
      title: "A real AI test with two assistants",
      body: "We ask ChatGPT and Claude real recommendation questions with web search enabled — including variants using your actual districts. The report shows “named in X of N queries”, the exact questions, and for each mention whether you were spoken of positively, neutrally or negatively."
    }, {
      kicker: "Step 3",
      title: "Compliance check including image rights",
      body: "Imprint, privacy policy, terms, tracking before consent, Google Fonts loaded from Google servers, embeds without consent — plus images whose origin points to a stock agency, checked by reverse image search with evidence links. This is not legal advice."
    }, {
      kicker: "Step 4",
      title: "Deep review and reputation analysis",
      body: "Not the five reviews Google shows, but up to 50 real reviews by date: how many arrived in the last 30, 90 and 365 days, the share of negative reviews, your reply rate and whether your replies read individual or templated, recurring praise and complaint themes with counts — and a named comparison with the strongest local competitors."
    }, {
      kicker: "Step 5",
      title: "Prioritised three-month plan",
      body: "Every finding ends in a concrete next step, sorted by impact and effort. A human reviews the plan before it reaches you."
    }],
    pillars: ["Built for local businesses in Germany, not for national online shops.", "Classic local visibility and AI visibility in a single score.", "Two AI assistants, real local questions — including the tone of each mention.", "German legal reality included: imprint, consent, image rights.", "A human reviews every report before it is sent.", "€39 one-off. No subscription, no contract."]
  },
  moreChecks: {
    title: "Also in the report",
    items: [{
      title: "Real customer questions",
      body: "The questions people actually type into Google around your service (from Google's own autocomplete), plus local variants per district — and which of them your website visibly answers. Every open question is a ready-made page or FAQ entry, and question-and-answer sections are quoted disproportionately often by AI assistants."
    }, {
      title: "Directories and social presence",
      body: "Which major German directories and platforms list you — Gelbe Seiten, Das Örtliche, 11880, Yelp and others."
    }, {
      title: "NAP consistency",
      body: "Whether your name, address and phone number match across those listings and profiles. Contradictory data weakens Google's trust in your location."
    }, {
      title: "Link structure",
      body: "Whether every important page is reachable from the main menu and whether internal links lead nowhere. Passed checks are reported just like errors."
    }, {
      title: "Referring domains",
      body: "How many independent websites link to your site — an authority signal, explicitly not a visitor counter."
    }]
  },
  platforms: {
    eyebrow: "Platform check",
    title: "Is your business listed correctly everywhere?",
    lead: "We don't just check where your business appears. The report also shows whether your name, address, phone number and opening hours match across important platforms.",
    checked: "checked",
    more: "+ 15 more directories and platforms"
  },
  how: {
    title: "How it works",
    steps: [{
      title: "Request the scan",
      body: "Business name and city are enough. No login, no access to your accounts required."
    }, {
      title: "Free summary report as a link",
      body: "Score, the six areas, Maps position, heatmap and the number of criteria passed per area."
    }, {
      title: "Detailed report with a 3-month plan",
      body: "Every single finding, all checks in detail and the prioritised plan — reviewed by a human."
    }]
  },
  report: {
    title: "What your report looks like",
    lead: "One link you can share — with your agency, your web contractor or your team.",
    tabs: ["Overview", "Visibility", "Reputation", "Legal & structure", "Action plan"],
    business: "Friseur Müller · Wuppertal-Elberfeld",
    note: "Illustrative example with sample values.",
    lockedRows: ["Finding: meta titles on 4 pages", "Finding: image source with 8 matches", "Finding: reply rate on reviews"],
    locked: "Included in the detailed report"
  },
  pricing: {
    title: "Pricing",
    lead: "One-off, no subscription, no contract.",
    free: {
      name: "Summary report",
      price: "free",
      items: ["Score out of 100 points", "The six areas as bars", "Google Maps position", "Visibility heatmap", "Number of directory listings", "NAP score", "Number of criteria passed per area (without saying which)"],
      cta: "Free check"
    },
    paid: {
      name: "Detailed report",
      price: "€39",
      priceNote: "one-off",
      items: ["Every single finding in plain language", "Complete directory list", "Where your contact details differ", "Compliance check including image-rights review", "Both AI visibility tests with tone per mention", "Deep review analysis with competitor table", "Prioritised three-month plan"],
      cta: "Request the report",
      note: "This is not legal advice."
    }
  },
  services: {
    freeName: "Free overview",
    reportPriceNote: "one-off",
    reportSlots: "Consulting month included for the first {total} customers: {n} of {total} places left.",
    reportCta: "Request the report",
    consultingName: "Consulting by ticket",
    consultingPriceNote: "for {days} days",
    consultingItems: ["Questions on every recommendation in your report", "Up to {max} tickets", "Reply within {hours} hours (Mon–Fri)", "In writing via ticket, no phone calls"],
    consultingNote: "Requires a detailed report.",
    consultingCta: "Request consulting",
    bandTitle: "Implementation / digital infrastructure",
    bandBody: "A new website or individual building blocks, such as Google profile, local listings, structured data or review set-up. Priced by individual quote.",
    bandNote: "No ranking promises: we implement what the report measurably shows as missing.",
    bandCta: "Request a quote"
  },
  quote: {
    metaTitle: "Request a quote — Positivup",
    title: "Request a quote",
    lead: "Briefly describe what you need. You receive an individual quote, no standard prices and no ranking promises.",
    fields: {
      name: "Name",
      company: "Company",
      email: "Email",
      phone: "Phone",
      scope: "Scope",
      items: "Which building blocks?",
      message: "Message"
    },
    optional: "optional",
    scopeFull: "Complete renewal (new website)",
    scopeItems: "Individual building blocks",
    itemLabels: {
      website: "Website",
      google_profile: "Google Business Profile",
      local_seo: "Local listings and directories",
      structured_data: "Structured data",
      reviews_setup: "Review set-up",
      ai_visibility: "Visibility in AI answers",
      other: "Other"
    },
    consent: "I agree that you may contact me about this quote.",
    consentLinkLabel: "Privacy policy",
    submit: "Send request",
    sending: "Sending …",
    successTitle: "Thank you, your request has reached us.",
    successBody: "We will get back to you by email with an individual quote.",
    failure: "The request could not be sent. Please check your entries and try again later.",
    errors: {
      name: "Please enter your name.",
      company: "Please enter your company.",
      email: "Please enter a valid email address.",
      items: "Please choose at least one building block.",
      consent: "Please agree to being contacted."
    }
  },
  form: {
    title: "Request the report",
    lead: "We'll come back to you with your summary report as a link. You then decide whether you want the detailed report.",
    fields: {
      company: "Business name",
      city: "City or postcode",
      website: "Website",
      email: "Email",
      phone: "Phone",
      message: "Message"
    },
    placeholders: {
      company: "Friseur Müller",
      city: "42103 Wuppertal",
      email: "name@company.com",
      website: "www.your-website.com",
      phone: "+49 202 123456"
    },
    optional: "optional",
    consent: "I agree that you may contact me about my report.",
    consentLinkLabel: "privacy policy",
    submit: "Send request",
    successTitle: "Thank you — your request is noted.",
    successBody: "You'll receive your free summary report as a link. If we have questions about your location, we'll get in touch briefly.",
    errors: {
      company: "Please enter your business name.",
      city: "Please enter a city or postcode.",
      email: "Please enter a valid email address.",
      consent: "Without your consent we cannot contact you."
    }
  },
  faq: {
    title: "Frequently asked questions",
    items: [{
      q: "How long does it take?",
      a: "Usually one to two working days. The scan itself is fast, but every report is reviewed by a human before it is sent."
    }, {
      q: "What data do you need from me?",
      a: "Business name and city are enough for the summary report. A website address makes the technical and legal review more precise. We never need access to your accounts."
    }, {
      q: "Is this GDPR compliant?",
      a: "We only evaluate publicly available information and use your contact details solely to send you your report. Details are in the privacy policy."
    }, {
      q: "What exactly is in the detailed report?",
      a: "Every single finding in plain language: the complete directory list, deviations in your contact details, the compliance check including image rights, both AI visibility tests with tone, the deep review analysis with competitor table and the prioritised three-month plan."
    }, {
      q: "Do I have to sign anything?",
      a: "No. The summary report is free, the detailed report costs €39 one-off. No subscription, no contract, no notice period."
    }, {
      q: "Can I pass the report to my own agency?",
      a: "Yes, explicitly. The report is written so your agency or web contractor can work with it directly — every finding names the concrete next step."
    }, {
      q: "Is this a legal review?",
      a: "No. We point out typical issues, such as missing mandatory details or images of unclear origin. This is not legal advice — for a binding assessment, please consult a lawyer."
    }, {
      q: "What is the visibility heatmap?",
      a: "Your Maps position is measured at many points across the city and shown in colour. That way you see in which districts you are found and in which you are not."
    }]
  },
  footer: {
    blurb: "Digital visibility for local businesses in Germany — measurable, understandable, without a subscription.",
    legal: [{
      label: "Imprint",
      to: "/impressum"
    }, {
      label: "Privacy policy",
      to: "/datenschutz"
    }, {
      label: "Terms",
      to: "/agb"
    }, {
      label: "Withdrawal",
      to: "/widerruf"
    }],
    rights: "All rights reserved.",
    disclaimer: "This is not legal advice."
  },
  heatmap: {
    title: "36 measuring points · search term “hairdresser”",
    avg: "Maps position (avg.)",
    organic: "Organic position",
    you: "Your location",
    legend: ["Rank 1–3", "Rank 4–7", "Rank 8+"]
  },
  social: {
    title: "References",
    body: "Real customer quotes and references will go here. Until then we deliberately leave this space empty — we don't invent them."
  },
  legal: {
    widerruf: {
      title: "Right of withdrawal",
      intro: "Information on the right of withdrawal for consumer contracts.",
      sections: [{
        heading: "Right of withdrawal",
        body: ["[Add withdrawal instructions and model withdrawal form for consumers.]"]
      }]
    },
    impressum: {
      title: "Imprint",
      intro: "Information pursuant to § 5 DDG (German Digital Services Act).",
      sections: [{
        heading: "Provider",
        body: ["[Company name / owner]", "[Street and number]", "[Postcode and city], Germany"]
      }, {
        heading: "Contact",
        body: ["Phone: [phone number]", "Email: [email address]"]
      }, {
        heading: "Represented by",
        body: ["[Name of authorised representative]"]
      }, {
        heading: "Register entry and VAT",
        body: ["Register court: [if applicable]", "Register number: [if applicable]", "VAT identification number pursuant to § 27 a UStG: [if applicable]"]
      }, {
        heading: "Responsible for the content",
        body: ["[Name], [address]"]
      }, {
        heading: "Dispute resolution",
        body: ["[Add a statement on participation or non-participation in consumer dispute resolution proceedings.]"]
      }]
    },
    datenschutz: {
      title: "Privacy policy",
      intro: "This website uses no analytics or tracking services and loads no fonts from external servers. We only collect personal data when you write to us through the request form.",
      sections: [{
        heading: "Controller",
        body: ["[Company name / owner], [address], [email address]"]
      }, {
        heading: "Request form",
        body: ["When you request a report we process your business name, city or postcode, email address and any voluntarily provided website, phone and message details. The sole purpose is producing and sending your report and any follow-up questions about it.", "The legal basis is your consent (Art. 6(1)(a) GDPR) and the initiation of a contractual relationship (Art. 6(1)(b) GDPR). You may withdraw your consent at any time."]
      }, {
        heading: "Collection of public information",
        body: ["For the report we evaluate publicly available information about the business you name, such as your website, business profile, reviews and directory listings."]
      }, {
        heading: "Retention period",
        body: ["[Add retention period and deletion policy.]"]
      }, {
        heading: "Processors and hosting",
        body: ["[Add hosting provider, email delivery and further service providers with location.]"]
      }, {
        heading: "Your rights",
        body: ["You have the right to access, rectification, erasure, restriction of processing, data portability and objection. You may also lodge a complaint with a data protection supervisory authority."]
      }, {
        heading: "Cookies",
        body: ["This website sets no cookies for analytics or marketing. Should such services be used in future, this will only happen after your explicit consent."]
      }]
    },
    agb: {
      title: "Terms and conditions",
      intro: "These terms apply to the free summary report and the detailed report.",
      sections: [{
        heading: "1. Scope of services",
        body: ["The summary report is free and contains the overall score, the six areas, the Google Maps position, the visibility heatmap, the number of directory listings, the NAP score and the number of criteria passed per area.", "The detailed report costs €39 one-off and additionally contains all individual findings and the prioritised three-month plan. No subscription and no contractual commitment arises."]
      }, {
        heading: "2. Conclusion of contract",
        body: ["[Add ordering process, conclusion of contract and payment methods.]"]
      }, {
        heading: "3. Right of withdrawal",
        body: ["[Add withdrawal instructions and model withdrawal form for consumers.]"]
      }, {
        heading: "4. Basis of the assessment",
        body: ["The assessment is based on publicly available information at the time of measurement. Rankings, reviews and answers from AI assistants change continuously; no specific ranking or business outcome is promised."]
      }, {
        heading: "5. No legal advice",
        body: ["Notes on imprint, data protection, consent and image rights are observations from an automated check with human review. This is not legal advice. For a binding assessment, please consult a lawyer."]
      }, {
        heading: "6. Use of the report",
        body: ["You may use the report freely within your company and pass it on to your agency or web contractor."]
      }, {
        heading: "7. Liability and final provisions",
        body: ["[Add liability provisions, applicable law and place of jurisdiction.]"]
      }]
    }
  }
};